import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import {
    PcgTableInput,
    PcgTableInputColumn,
} from '../../shared/table/table.component';
import { param } from '../../shared/http-params';
import { EditUserVm } from '../../shared/generated/Administration/Models/EditUserVm';
import { BarcodeVm } from '../../shared/generated/Administration/Models/ErfsManagement/Barcodes/BarcodeVm';
import { DispensingEventVm } from '../../shared/generated/Events/Models/DispensingEventVm';
import { EditEmergencyEventVm } from '../../shared/generated/Administration/Models/EditEmergencyEventVm';
import { AssetDetailVm } from '../../shared/generated/Administration/Models/Assets/AssetDetailVm';
import { ClientDetailVm } from '../../shared/generated/Administration/Models/Clients/ClientDetailVm';
import { UnitVm } from '../../shared/generated/Administration/Models/UnitVm';
import { AppDatabase } from '../../../local-db/services/init.ldb.service';

@Injectable({ providedIn: 'root' })
export class CachePrimerService {
    private http = inject(HttpClient);
    private localDb = inject(AppDatabase);

    tableInput: PcgTableInput = {
        columns: null!,
        start: 0,
        length: 100,
        searchText: '',
        exactMatch: false,
        exportType: null,
        reportName: null,
    };

    clientIds: number[] = [];

    primeCache() {
        this.primeUserListCache();
        this.primeDispenseEventListCache();
        this.primeBarcodeListCache();
        this.primeEmergencyEventListCache();
        this.primeAssetListCache();
        this.primeClientListCache();
        this.primeUnitListCache();
        this.primeSelectControllerCache();
    }

    setPcgTableColumns(
        vm: any,
        sortField: string,
        sortDirection: string = 'asc'
    ) {
        let perColumnSearchSource = new BehaviorSubject<string[]>([]);
        perColumnSearchSource.next(
            Array.from(
                new Array(Array.from(vm.ColumnDefs.keys()).length),
                () => ''
            )
        );
        let perColSearches = perColumnSearchSource.value;

        const columns: PcgTableInputColumn[] = [];
        const colNames = Array.from(vm.ColumnDefs.keys());
        for (let i = 0; i < perColSearches.length; ++i) {
            const theCol: PcgTableInputColumn = {
                searchText: perColSearches[i],
            };
            const sortData = [[sortField, sortDirection]];
            const colName = colNames[i];
            const mySort = sortData.find((o) => o[0] === colName);
            if (mySort) {
                theCol.sortColumnNum = 0;
                theCol.sortDirection = mySort[1];
            } else {
                theCol.sortColumnNum = null!;
                theCol.sortDirection = null!;
            }
            columns.push(theCol);
        }
        return columns;
    }

    primeUserListCache() {
        const columns = this.setPcgTableColumns(EditUserVm, 'activeStatus');

        let filters = {
            activeStatus: null,
            isActive: true,
        };
        let userAjaxDataString = param(filters);

        this.http
            .get(
                `api/Administration/Users/GetUserList` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.EditUserTbl.put({
                        userId: d.data[x].userId,
                        firstName: d.data[x].firstName,
                        lastName: d.data[x].lastName,
                        userName: d.data[x].userName,
                        phoneNumber: d.data[x].phoneNumber,
                        email: d.data[x].email,
                        isActive: d.data[x].isActive,
                        isActiveStr: d.data[x].isActiveStr,
                        isLockedOut: d.data[x].isLockedOut,
                        isLockedOutStr: d.data[x].isLockedOutStr,
                        friendlyId: d.data[x].friendlyId,
                        address: d.data[x].address,
                        addressFull: d.data[x].addressFull,
                        domicileId: d.data[x].domicileId,
                        domicile: d.data[x].domicile,
                        activeStatus: d.data[x].activeStatus,
                        role: d.data[x].role,
                        isTeamLead: d.data[x].isTeamLead,
                        name: d.data[x].name,
                        experienceLevel: d.data[x].experienceLevel,
                        isW9Received: d.data[x].isW9Received,
                        isResponseTrained: d.data[x].isResponseTrained,
                        toursOfDuty: d.data[x].toursOfDuty,
                        employeeType: d.data[x].employeeType,
                        driversLicenseClass: d.data[x].driversLicenseClass,
                        hasHazMat: d.data[x].hasHazMat,
                        lastWebLogin: d.data[x].lastWebLogin,
                        lastMobileLogin: d.data[x].lastMobileLogin,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                        canDelete: d.data[x].canDelete,
                    });
                }
            });
    }

    primeSelectControllerCache() {
        this.http.get(`api/Select/Clients`).subscribe((d: any) => {});
        this.http.get(`api/Select/SiteLocations`).subscribe((d: any) => {});
        this.http
            .get(`api/Select/SiteLocations?clientId=null`)
            .subscribe((d: any) => {});
        this.http.get(`api/Select/EmergencyEvents`).subscribe((d: any) => {});
        this.http.get(`api/Select/Assets`).subscribe((d: any) => {});
        this.http.get(`api/Select/Units`).subscribe((d: any) => {});
        this.http
            .get(`api/Select/StatesStringReference`)
            .subscribe((d: any) => {});
        this.http.get(`api/Select/Permissions`).subscribe((d: any) => {});
        this.http.get(`api/Select/Domiciles`).subscribe((d: any) => {});
        this.http.get(`api/Select/StatusEnums`).subscribe((d: any) => {});
        this.http.get(`api/Select/AssetTypes`).subscribe((d: any) => {});
        this.http.get(`api/Select/FuelTypes`).subscribe((d: any) => {});
    }

    primeBarcodeListCache() {
        const columns = this.setPcgTableColumns(BarcodeVm, 'barcodeId');
        let userAjaxDataString = '';

        this.http
            .get(
                `api/Administration/Barcode/GetAjaxList` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.BarcodeTbl.put({
                        barcodeId: d.data[x]?.barcodeId,
                        offlineId: d.data[x]?.offlineId,
                        barcodeString: d.data[x]?.barcodeString,
                        tagNumber: d.data[x]?.tagNumber,
                        unit: d.data[x]?.unit,
                        unitId: d.data[x]?.unitId,
                        client: d.data[x]?.client,
                        clientId: d.data[x]?.clientId,
                        canDelete: d.data[x]?.canDelete,
                        userId: d.data[x]?.userId,
                        isUpdatedOffline: d.data[x]?.isUpdatedOffline,
                    });
                }
            });
    }

    primeDispenseEventListCache() {
        const columns = this.setPcgTableColumns(
            DispensingEventVm,
            'dispenseDate',
            'desc'
        );
        let filters = { dateOccurred: null };
        let userAjaxDataString = param(filters);

        this.http
            .get(
                `api/Events/DispensingEventList/GetDispensingEventListVm` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.DispensingEventTbl.put({
                        dispenseDate: d.data[x].dispenseDate,
                        dispenseDateLocal: d.data[x].dispenseDateLocal,
                        strDispenseDateTime: d.data[x].strDispenseDateTime,
                        client: d.data[x].client,
                        tankAssetNumber: d.data[x].tankAssetNumber,
                        gallons: d.data[x].gallons,
                        personnel: d.data[x].personnel,
                        emergencyEvent: d.data[x].emergencyEvent,
                        barcodeId: d.data[x].barcodeId,
                        id: d.data[x].id,
                        barcodeString: d.data[x].barcodeString,
                        tagNumber: d.data[x].tagNumber,
                        clientId: d.data[x].clientId,
                        unitId: d.data[x].unitId,
                        emergencyEventId: d.data[x].emergencyEventId,
                        assetId: d.data[x].assetId,
                        siteLocationId: d.data[x].siteLocationId,
                        asset: d.data[x].asset,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                        fuelTypeId: d.data[x].fuelTypeId,
                        secondaryFuelTypeId: d.data[x].secondaryFuelTypeId,
                        secondaryFuelType: d.data[x].secondaryFuelType,
                        secondaryGallons: d.data[x].secondaryGallons,
                    });
                }
            });
    }

    primeEmergencyEventListCache() {
        const columns = this.setPcgTableColumns(EditEmergencyEventVm, 'name');
        let filters = { isActive: true };
        let userAjaxDataString = param(filters);

        this.http
            .get(
                `api/Administration/EmergencyEvent/GetEmergencyEventsList` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.EditEmergencyEventTbl.put({
                        emergencyEventId: d.data[x].emergencyEventId,
                        name: d.data[x].name,
                        location: d.data[x].location,
                        latitude: d.data[x].latitude,
                        longitude: d.data[x].longitude,
                        eventDate: d.data[x].eventDate,
                        isActive: d.data[x].isActive,
                        strEventDate: d.data[x].strEventDate,
                        strIsActive: d.data[x].strIsActive,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                    });
                }
            });
    }

    primeAssetListCache() {
        const columns = this.setPcgTableColumns(
            AssetDetailVm,
            'tankAssetNumber',
            'asc'
        );
        let userAjaxDataString = '';

        this.http
            .get(
                `api/Administration/AssetList/GetAssetListVm` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.AssetDetailTbl.put({
                        assetId: d.data[x].assetId,
                        assetTypeId: d.data[x].assetTypeId,
                        tankAssetNumber: d.data[x].tankAssetNumber,
                        assetType: d.data[x].assetType,
                        domicile: d.data[x].domicile,
                        domicileId: d.data[x].domicileId,
                        status: d.data[x].status,
                        statusStr: d.data[x].statusStr,
                        imageFileName: d.data[x].imageFileName,
                        thumbnailFileName: d.data[x].thumbnailFileName,
                        companyOwned: d.data[x].companyOwned,
                        billableRatePerHour: d.data[x].billableRatePerHour,
                        file: d.data[x].file,
                        dateOfExpectedRepair: d.data[x].dateOfExpectedRepair,
                        subContracted: d.data[x].subContracted,
                        loadBearing: d.data[x].loadBearing,
                        powerTakeOff: d.data[x].powerTakeOff,
                        notes: d.data[x].notes,
                        siteLocation: d.data[x].siteLocation,
                        siteId: d.data[x].siteId,
                        assignedSiteLocation: d.data[x].assignedSiteLocation,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                        canDelete: d.data[x].canDelete,
                    });
                }
            });
    }

    primeClientListCache() {
        const columns = this.setPcgTableColumns(ClientDetailVm, 'name', 'asc');
        let filters = { isActive: true };
        let userAjaxDataString = param(filters);
        this.clientIds = [];

        this.http
            .get(
                `api/Administration/ClientList/GetClientListVm` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.ClientDetailTbl.put({
                        clientId: d.data[x].clientId,
                        clientName: d.data[x].clientName,
                        isActiveStr: d.data[x].isActiveStr,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                        canDelete: d.data[x].canDelete,
                    });
                    if (d?.data[x]?.clientId > 0) {
                        this.clientIds.push(d.data[x].clientId);
                    }
                }
            });

        if (this.clientIds?.length > 0) {
            for (let x = 0; x < this.clientIds.length; x++) {
                this.http
                    .get(
                        `api/Select/SiteLocations?clientId=${this.clientIds[x]}`
                    )
                    .subscribe((lst: any) => {});
            }
        }
    }

    primeUnitListCache() {
        const columns = this.setPcgTableColumns(UnitVm, 'name');
        let filters = { isActive: true };
        let userAjaxDataString = param(filters);

        this.http
            .get(
                `api/Administration/Unit/GetUnitList` +
                    `?${param(this.tableInput, true)}` +
                    `&${userAjaxDataString}` +
                    `&excelPassword=${encodeURIComponent('')}` +
                    `&columnJson=${encodeURIComponent(this.jsonStringify(columns))}`
            )
            .subscribe((d: any) => {
                for (let x = 0; x < d.data.length; x++) {
                    this.localDb.UnitTbl.put({
                        unitId: d.data[x].unitId,
                        name: d.data[x].name,
                        isActiveStr: d.data[x].isActiveStr,
                        isActive: d.data[x].isActive,
                        fuelTypeId: d.data[x].fuelTypeId,
                        fuelTypeName: d.data[x].fuelTypeName,
                        isUpdatedOffline: d.data[x].isUpdatedOffline,
                        canDelete: d.data[x].canDelete,
                    });
                }
            });
    }

    jsonStringify(columns: any) {
        return JSON.stringify(
            columns.map(
                (
                    o: {
                        sortColumnNum: null;
                        searchText: string;
                        sortDirection: null;
                    },
                    index: number
                ) => {
                    const newObj: any = {};
                    if (index > 0) {
                        o.sortColumnNum = null;
                    }
                    if (o.searchText !== null && o.searchText !== '') {
                        newObj.searchText = o.searchText;
                    }
                    if (o.sortColumnNum !== null) {
                        newObj.sortColumnNum = o.sortColumnNum;
                    }
                    if (o.sortDirection !== null) {
                        newObj.sortDirection = o.sortDirection;
                    }
                    return newObj;
                }
            )
        );
    }
}
